import { keepPreviousData, useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { API_TAGS } from '@/shared/api';
import { createUserGroup, deleteUserGroup, fetchUserGroupById, fetchUserGroups, updateUserGroup } from './api';
import { UserGroupCreateRequest, UserGroupUpdateRequest } from './types';

export const userGroupKeys = {
  all: [API_TAGS.USER_GROUPS] as const,
  lists: () => [...userGroupKeys.all, 'list'] as const,
  list: (filters: { limit?: number; offset?: number } = {}) => [...userGroupKeys.lists(), { ...filters }] as const,
  details: () => [...userGroupKeys.all, 'detail'] as const,
  detail: (id: number) => [...userGroupKeys.details(), id] as const,
};

export const useUserGroups = (filters: { limit?: number; offset?: number; isDeleted?: boolean } = {}) => {
  return useQuery({
    queryKey: userGroupKeys.list(filters),
    queryFn: () => fetchUserGroups(filters),
  });
};

export const useInfiniteUserGroups = (params: { limit?: number; offset?: number } = {}) => {
  const limit = params.limit || 50;
  const initialPageParam = Math.floor((params?.offset || 0) / limit);

  const query = useInfiniteQuery({
    queryKey: userGroupKeys.list(params),
    queryFn: ({ pageParam }) => fetchUserGroups({ ...params, limit, offset: pageParam * limit }),
    initialPageParam,
    placeholderData: keepPreviousData,
    getNextPageParam: (lastPage, _allPages, lastPageParam) => {
      return (lastPage.total || 0) <= limit * (lastPageParam + 1) ? undefined : lastPageParam + 1;
    },
  });

  const data = {
    total: query.data?.pages[0]?.total,
    userGroups: query.data?.pages.flatMap((p) => p.userGroups) || [],
  };

  return { ...query, data };
};

export const useUserGroupById = (id?: number) => {
  return useQuery({
    queryKey: userGroupKeys.detail(id!),
    queryFn: () => fetchUserGroupById(id!),
    enabled: !!id,
  });
};

export const useCreateUserGroup = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: UserGroupCreateRequest) => createUserGroup(payload),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: userGroupKeys.lists() });
      // Set the new group in cache if we have the detail data
      if (data.id) {
        // We don't have full detail data from create response, so just invalidate
        queryClient.invalidateQueries({ queryKey: userGroupKeys.detail(data.id) });
      }
    },
  });
};

export const useUpdateUserGroup = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: { id: number; body: UserGroupUpdateRequest }) => updateUserGroup(params),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: userGroupKeys.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: userGroupKeys.lists() });
    },
  });
};

export const useDeleteUserGroup = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => deleteUserGroup(id),
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({ queryKey: userGroupKeys.lists() });
      // Remove the deleted group from cache
      queryClient.removeQueries({ queryKey: userGroupKeys.detail(id) });
    },
  });
};
